/**
 * Azure Search Proxy Service
 *
 * Routes all Azure Search requests through the backend API so that MI tokens
 * never leave the server.  The backend SearchProxyController forwards the
 * request to Azure Search using its own Managed Identity credential.
 */

import { Store } from 'azsearchstore';
import { nextGenService } from './NextGenService';

/**
 * Azure Search Authentication Service
 * Now proxies all search/suggest operations through the backend API.
 * MI tokens are never exposed to the client.
 */
class AzureSearchAuthService {

    constructor() {
    }

    /**
     * Custom search callback for AzSearchStore.
     * Makes the search request through the backend proxy, dispatches results
     * into the store, and returns a special marker so the store skips its
     * own HTTP call.
     */
    public async searchCallback(state: Store.SearchState, postBody: { [key: string]: any }): Promise<any> {
        try {
            const indexName = state.config.index;

            const proxyResponse = await nextGenService.proxySearchRequest(indexName, postBody);

            if (proxyResponse.status === 200 && proxyResponse.data) {
                // Dispatch results into the Redux store if accessible
                const anyState = state as any;
                const reduxStore =
                    anyState.config?.store ||
                    anyState.store ||
                    anyState._store ||
                    anyState.config?._store;

                if (reduxStore && typeof reduxStore.dispatch === 'function') {
                    reduxStore.dispatch({
                        type: 'RECEIVE_RESULTS',
                        results: proxyResponse.data,
                        receivedAt: Date.now()
                    });
                }
            }

            // Return headers — the store may still attempt its own fetch but without
            // a valid token the call will fail; the dispatched results above are
            // the authoritative source.
            return {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            };

        } catch (error) {
            return {
                'error': 'auth_failed',
                'Content-Type': 'application/json'
            };
        }
    }

    /**
     * Custom suggest callback for AzSearchStore.
     * Proxies the suggest request through the backend API.
     */
    public async suggestCallback(state: Store.SearchState, postBody: { [key: string]: any }): Promise<any> {
        try {
            await this.dispatchDirectSuggestResults(state);

            return {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            };

        } catch (error) {
            return {
                'error': 'auth_failed',
                'Content-Type': 'application/json'
            };
        }
    }

    /**
     * Makes a suggest request through the backend proxy and dispatches the
     * results into the store / via custom events.
     */
    private async dispatchDirectSuggestResults(state: Store.SearchState): Promise<void> {
        try {
            if (!state.parameters?.input || state.parameters.input.length < 3) {
                return;
            }

            const indexName = state.config.index;
            const params = state.parameters.suggestionsParameters || {};
            const input = state.parameters.input;

            const postBody = {
                search: input,
                suggesterName: params.suggesterName ?? 'mdppsuggester',
                select: params.select ?? 'OrganizationName',
                top: params.top ?? 50,
                filter: params.filter || '',
                fuzzy: params.fuzzy || false
            };

            const proxyResponse = await nextGenService.proxySuggestRequest(indexName, postBody);

            if (!proxyResponse.data || !proxyResponse.data.value) {
                return;
            }

            const directResults = proxyResponse.data;

            // Import event constants from debug utilities
            const { AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT, AZURE_SEARCH_FORCE_UPDATE_EVENT } =
                await import('./AzureSearchUtil');

            // Dispatch a custom event so the UI picks up the suggestions
            const suggestionEvent = new CustomEvent(AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT, {
                detail: {
                    suggestions: directResults.value,
                    input: input,
                    service: state.config.service,
                    index: indexName
                },
                bubbles: true,
                cancelable: true
            });

            document.dispatchEvent(suggestionEvent);

            // Try to inject into Redux store to clear the fetching state
            try {
                const anyState = state as any;
                const reduxStore =
                    anyState.config?.store ||
                    anyState.store ||
                    anyState._store ||
                    anyState.config?._store;

                if (reduxStore && typeof reduxStore.dispatch === 'function') {
                    reduxStore.dispatch({
                        type: 'RECEIVE_SUGGESTIONS',
                        suggestions: { value: directResults.value },
                        receivedAt: Date.now()
                    });
                }
            } catch (reduxError) {
                // Intentionally swallowed — event dispatch above is the primary path
            }

            // Fallback: force-update if store is still fetching after 1 second
            setTimeout(() => {
                if ((state as any).suggestions && (state as any).suggestions.isFetching) {
                    const forceEvent = new CustomEvent(AZURE_SEARCH_FORCE_UPDATE_EVENT, {
                        detail: {
                            suggestions: directResults.value,
                            forceUpdate: true
                        },
                        bubbles: true,
                        cancelable: true
                    });

                    document.dispatchEvent(forceEvent);
                }
            }, 1000);

        } catch (error) {
            // Intentionally swallowed — suggestion dispatch is best-effort
        }
    }

    /**
     * @deprecated MI tokens are no longer returned to clients.
     * Callers should use nextGenService.proxySuggestRequest / proxySearchRequest instead.
     */
    public async getSearchAccessToken(): Promise<string> {
        throw new Error('getSearchAccessToken has been removed. Use server-side proxy endpoints instead.');
    }

    /**
     * Clear token cache — now a no-op since tokens are managed server-side.
     */
    public clearTokenCache(): void {
        // No client-side token cache to clear
    }
}

// Export singleton instance
export const azureSearchAuthService = new AzureSearchAuthService();
export default azureSearchAuthService;